import React, { useState, useRef } from "react";
import InputBox from "../Components/InputBox";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  addNewDoc,
  setDocData,
  storage,
} from "../../../utils/firebase/Firebase.utils";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import SelectInput from "../Components/SelectInput";
import { FiSave, FiX, FiUpload, FiImage, FiCalendar } from "react-icons/fi";
import { motion } from "framer-motion";

export default function AnnouncementForm({
  data,
  setAddMode,
  logAnnouncementActivity,
}) {
  const uploadingRef = useRef(null);
  const currentUser = useSelector((state) => state.userObject);
  const [formData, setFormData] = useState(data || {});
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const storageRef = ref(storage, `announcements/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {
        toast.error("Upload failed. Please try again.");
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setFormData((prev) => ({ ...prev, poster: downloadURL }));
          toast.success("Image uploaded successfully!");
        });
      }
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Check if scheduled publication date is valid
      const isScheduled =
        formData.scheduledPublishDate &&
        new Date(formData.scheduledPublishDate) > new Date();
      const status = isScheduled ? "scheduled" : "unpublished";

      if (data) {
        await setDocData("announcements", data.id, {
          ...formData,
          status: formData.status || status,
        });

        if (logAnnouncementActivity) {
          await logAnnouncementActivity(
            {
              ...formData,
              id: data.id,
              title: formData.title || data.title,
              type: formData.type || data.type,
            },
            "updated"
          );
        }

        toast.success("Announcement updated successfully");
        setIsProcessing(false);
        setAddMode(false);
        return;
      }

      const docObject = {
        ...formData,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.email,
        user: currentUser.uid,
        status: status,
      };

      const newDocRef = await addNewDoc("announcements", docObject);

      if (logAnnouncementActivity && newDocRef) {
        await logAnnouncementActivity(
          {
            ...docObject,
            id: newDocRef.id,
            title: formData.title,
            type: formData.type,
          },
          "created"
        );
      }

      toast.success("Announcement added successfully");
      setAddMode(false);
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      setIsProcessing(false);
    }
  };

  const typeOptions = [
    { value: "announcements", label: "Announcements" },
    { value: "newsletters", label: "Newsletters" },
    { value: "events", label: "Events" },
    { value: "circulars", label: "Circulars" },
    { value: "advisory", label: "Advisory" },
    { value: "others", label: "Others" },
  ];

  // Get minimum date (today) for the date picker
  const today = new Date().toISOString().split("T")[0];

  return (
    <motion.div
      className="bg-white rounded-xl overflow-hidden w-full max-w-3xl border shadow-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="border-b p-4 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-800">
            {data ? "Edit Announcement" : "Create Announcement"}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {data
              ? "Update the announcement details below"
              : "Fill in the details to create a new announcement"}
          </p>
        </div>
        <button
          className="text-gray-500 hover:text-gray-700 transition-colors"
          onClick={() => setAddMode(false)}
          aria-label="Close form"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 text-left">
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Type
              </label>
              <SelectInput
                name="type"
                label=""
                options={typeOptions}
                required
                onChange={(selected) =>
                  setFormData({ ...formData, type: selected.value })
                }
                value={typeOptions.find((item) => item.value === formData.type)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                <FiCalendar className="w-3 h-3" />
                Scheduled Publish Date
              </label>
              <input
                type="date"
                name="scheduledPublishDate"
                min={today}
                onChange={handleChange}
                value={formData.scheduledPublishDate || ""}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 transition-colors"
              />
              <p className="text-xs text-gray-500 mt-0.5">
                Leave empty to keep as unpublished
              </p>
            </div>

            <div className="md:col-span-2">
              <InputBox
                name="title"
                label="Title"
                type="text"
                placeholder="Enter a descriptive title"
                required
                onChange={handleChange}
                value={formData.title || ""}
              />
            </div>

            <div className="md:col-span-2">
              <InputBox
                name="description"
                label="Short Description"
                type="text"
                placeholder="Enter a brief description"
                required
                onChange={handleChange}
                value={formData.description || ""}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Detailed Content
              </label>
              <textarea
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 transition-colors min-h-[120px]"
                name="Details"
                placeholder="Enter the full content of your announcement"
                onChange={handleChange}
                required
                value={formData.Details || ""}
              ></textarea>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Featured Image
              </label>
              <div
                className={`border-2 border-dashed rounded-lg transition-all cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500 ${
                  formData?.poster ? "border-green-500" : "border-gray-300"
                }`}
                onClick={() => uploadingRef.current.click()}
              >
                {formData?.poster ? (
                  <div className="relative group">
                    <img
                      src={formData.poster}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                      <FiUpload className="text-white w-6 h-6" />
                    </div>
                  </div>
                ) : (
                  <div className="h-48 flex flex-col items-center justify-center p-4 text-center">
                    <FiImage className="w-8 h-8 text-gray-400 mb-2" />
                    <div className="text-sm text-gray-700 font-medium mb-1">
                      Drop your image here, or click to browse
                    </div>
                    <p className="text-xs text-gray-500">
                      Supports JPG, PNG, GIF up to 10MB
                    </p>
                  </div>
                )}
              </div>

              <input
                type="file"
                ref={uploadingRef}
                onChange={handleUpload}
                name="poster"
                className="hidden"
                accept="image/*"
              />

              {progress > 0 && progress < 100 && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-gray-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{`Uploading: ${Math.round(
                    progress
                  )}%`}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-3 border-t mt-4">
            <button
              type="button"
              onClick={() => setAddMode(false)}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-md shadow-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              className="px-3 py-1.5 text-xs bg-gray-700 text-white rounded-md shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              type="submit"
              disabled={isProcessing}
            >
              <FiSave className="w-3 h-3" />
              <span>
                {isProcessing ? "Processing..." : data ? "Update" : "Create"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
